import { useMemo, useState } from 'react'
import { Outlet, useMatch } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { ProjectFilter } from '@/components/projects/ProjectFilter'
import { ProjectGrid } from '@/components/projects/ProjectGrid'
import { getProject, projects } from '@/content/projects'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import type { Tag } from '@/types/content'
import styles from './Page.module.css'

export function ProjectsPage() {
  const [activeTag, setActiveTag] = useState<Tag | null>(null)

  // This page owns the tab title for the whole `/projects` branch. React runs
  // child effects before parent ones, so if the modal set the title too, this
  // component would immediately overwrite it.
  const openProject = useMatch('/projects/:slug')
  const project = getProject(openProject?.params.slug)
  useDocumentTitle(
    openProject ? (project?.title ?? 'Project not found') : 'Projects',
  )

  const visible = useMemo(
    () => (activeTag ? projects.filter((p) => p.tags.includes(activeTag)) : projects),
    [activeTag],
  )

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Work"
        title="Projects"
        lede="Design, CAD, manufacturing, and robotics work — from course projects and competition robots to internships and certifications."
      >
        <ProjectFilter
          active={activeTag}
          onChange={setActiveTag}
          total={projects.length}
        />
      </PageHeader>

      <p className={styles.resultCount} aria-live="polite">
        {visible.length} {visible.length === 1 ? 'project' : 'projects'}
        {activeTag ? ` tagged ${activeTag}` : ''}
      </p>

      <ProjectGrid projects={visible} />

      {/* `/projects/:slug` renders the project modal here. */}
      <Outlet />
    </div>
  )
}
